import { useEffect, useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { spendDataEnrichmentService } from "../../../../services/spendDataEnrichmentServices";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../components/ui/pagination";

const AI_GENERATED_COLUMNS = [
  "L1",
  "L2",
  "L3",
  "Family (L1)",
  "Class (L2)",
  "Commodity (L3)",
  "Family (L1) Confidence score",
  "Class (L2) Confidence score",
  "Commodity (L3) Confidence score",
  "Family (L1) Code",
  "Class (L2) Code",
  "Commodity (L3) Code",
  "Supplier Industry",
  "Supplier_Industry",
  "supplier_industry",
];

// The agent's actual field names don't always match the display names exactly
// (spaces vs underscores, with/without parentheses) — each group below lists
// the spellings we've seen, in priority order, so we can find whichever one
// is really in the data.
const PRIORITY_COLUMN_GROUPS: string[][] = [
  ["Supplier Name", "Supplier_Name", "supplier_name", "Supplied_Name"],
  ["Supplier Industry", "Supplier_Industry", "supplier_industry"],
  ["Item Description", "Item_Description", "item_description"],
  ["Family (L1)", "Family(L1)", "Family_L1", "Family_(L1)"],
  ["Family (L1) Confidence score", "Family (L1) Confidence", "Family_L1_Confidence", "Family_(L1)_Confidence"],
  ["Class (L2)", "Class(L2)", "Class_L2", "Class_(L2)"],
  ["Class (L2) Confidence score", "Class (L2) Confidence", "Class_L2_Confidence", "Class_(L2)_Confidence"],
  ["Commodity (L3)", "Commodity(L3)", "Commodity_L3", "Commodity_(L3)"],
  ["Commodity (L3) Confidence score", "Commodity (L3) Confidence", "Commodity_L3_Confidence", "Commodity_(L3)_Confidence"],
  ["Family (L1) Code", "Family(L1) Code", "Family_L1_Code", "Family_(L1)_Code"],
  ["Class (L2) Code", "Class(L2) Code", "Class_L2_Code", "Class_(L2)_Code"],
  ["Commodity (L3) Code", "Commodity(L3) Code", "Commodity_L3_Code", "Commodity_(L3)_Code"],
];

// Raw category codes — once the text columns above are showing, the codes are redundant.
const CODE_COLUMNS_TO_HIDE = ["L1", "L2", "L3"];

const reorderColumns = (allColumns: string[]): string[] => {
  const present = new Set(allColumns);
  const used = new Set<string>();
  const ordered: string[] = [];

  PRIORITY_COLUMN_GROUPS.forEach((candidates) => {
    const match = candidates.find((candidate) => present.has(candidate));
    if (match) {
      ordered.push(match);
      used.add(match);
    }
  });

  const remaining = allColumns.filter(
    (column) => !used.has(column) && !CODE_COLUMNS_TO_HIDE.includes(column)
  );

  return [...ordered, ...remaining];
};

type SupplierClassificationStepProps = {
  data?: unknown;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
};

type TableRow = {
  [key: string]: any;
  id?: string;
};

type HierarchyNode = {
  id: string;
  label: string;
  count: string;
  level: number;
  children?: HierarchyNode[];
};

const taxonomyHierarchy: HierarchyNode[] = [
  {
    id: "direct-spend",
    label: "Direct Spend",
    count: "4,123",
    level: 0,
    children: [
      {
        id: "raw-materials",
        label: "Raw Materials",
        count: "11,842",
        level: 1,
      },
      {
        id: "packaging",
        label: "Packaging",
        count: "11,842",
        level: 1,
      },
      {
        id: "components",
        label: "Components",
        count: "11,842",
        level: 1,
      },
    ],
  },
  {
    id: "indirect-spend",
    label: "Indirect Spend",
    count: "5,789",
    level: 0,
    children: [
      {
        id: "technology",
        label: "Technology",
        count: "2,356",
        level: 1,
        children: [
          {
            id: "software",
            label: "Software",
            count: "854",
            level: 2,
            children: [
              {
                id: "saas",
                label: "SaaS Licenses",
                count: "2,345",
                level: 3,
              },
            ],
          },
          {
            id: "hardware",
            label: "Hardware",
            count: "612",
            level: 2,
          },
          {
            id: "cloud-infra",
            label: "Cloud Infra",
            count: "488",
            level: 2,
          },
        ],
      },
      {
        id: "professional-services",
        label: "Professional services",
        count: "1,684",
        level: 1,
      },
      {
        id: "logistics",
        label: "Logistics",
        count: "1,042",
        level: 1,
      },
      {
        id: "facilities",
        label: "Facilities",
        count: "707",
        level: 1,
      },
    ],
  },
];

const buildExpandedState = (
  nodes: HierarchyNode[]
): Record<string, boolean> => {
  return nodes.reduce<Record<string, boolean>>((acc, node) => {
    acc[node.id] = true;

    if (node.children?.length) {
      Object.assign(acc, buildExpandedState(node.children));
    }

    return acc;
  }, {});
};

const isPlainObject = (value: unknown): value is Record<string, any> => {
  return !!value && typeof value === "object" && !Array.isArray(value);
};

const isObjectRowArray = (value: unknown): value is TableRow[] => {
  return (
    Array.isArray(value) &&
    value.every(
      (item) => !!item && typeof item === "object" && !Array.isArray(item)
    )
  );
};

const normalizeClassificationRows = (value: unknown): TableRow[] => {
  if (!value) return [];

  if (typeof value === "string") {
    try {
      return normalizeClassificationRows(JSON.parse(value));
    } catch {
      return [];
    }
  }

  if (isObjectRowArray(value)) {
    return value;
  }

  if (!isPlainObject(value)) {
    return [];
  }

  const obj = value as Record<string, any>;

  if (isObjectRowArray(obj.output)) return obj.output;
  if (isObjectRowArray(obj.data?.output)) return obj.data.output;
  if (isObjectRowArray(obj.data?.output?.data)) return obj.data.output.data;
  if (isObjectRowArray(obj.data?.output?.foreach)) return obj.data.output.foreach;
  if (isObjectRowArray(obj.data?.Data_Table)) return obj.data.Data_Table;
  if (isObjectRowArray(obj.Data_Table)) return obj.Data_Table;
  if (isObjectRowArray(obj.data?.input_data)) return obj.data.input_data;
  if (isObjectRowArray(obj.input_data)) return obj.input_data;
  if (isObjectRowArray(obj.data)) return obj.data;
  if (isObjectRowArray(obj.result)) return obj.result;
  if (isObjectRowArray(obj.response)) return obj.response;
  if (isObjectRowArray(obj.payload)) return obj.payload;

  if (isPlainObject(obj.data)) {
    const rows = normalizeClassificationRows(obj.data);
    if (rows.length > 0) return rows;
  }

  if (isPlainObject(obj.result)) {
    const rows = normalizeClassificationRows(obj.result);
    if (rows.length > 0) return rows;
  }

  if (isPlainObject(obj.response)) {
    const rows = normalizeClassificationRows(obj.response);
    if (rows.length > 0) return rows;
  }

  if (isPlainObject(obj.payload)) {
    const rows = normalizeClassificationRows(obj.payload);
    if (rows.length > 0) return rows;
  }

  return [];
};

const getColumnsFromRows = (rows: TableRow[]) => {
  const columnSet = new Set<string>();

  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (key !== "id") {
        columnSet.add(key);
      }
    });
  });

  return Array.from(columnSet);
};

const renderCellValue = (value: any) => {
  if (value === null || value === undefined) return "";

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

export function SupplierClassificationStep({
  data,
  onNext,
  onBack,
  onCancel,
}: SupplierClassificationStepProps) {
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>(
    () => buildExpandedState(taxonomyHierarchy)
  );

  const [activeNodeId, setActiveNodeId] = useState<string>("direct-spend");
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    // Sole source: the latest row in spend_data_enrichment_results. Nothing is
    // rendered from the pipeline payload, so what the grid shows is always what
    // the database holds.
    const loadFromEnrichmentTable = async () => {
      try {
        const record = await spendDataEnrichmentService.getLatestRecord();
        const enrichmentRows = normalizeClassificationRows(record?.response_data);

        console.log("✅ ENRICHMENT TABLE ROW COUNT:", enrichmentRows.length);

        setTableData(enrichmentRows);
      } catch (error) {
        console.log("⚠️ ENRICHMENT TABLE FETCH FAILED:", error);
        setTableData([]);
      }
    };

    loadFromEnrichmentTable();
  }, []);

  const columns = reorderColumns(getColumnsFromRows(tableData));

  const aiGeneratedColumns = new Set(
    columns.filter((column) => AI_GENERATED_COLUMNS.includes(column))
  );

  useEffect(() => {
    setPage(1);
  }, [tableData.length, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(tableData.length / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const pageStartIndex = (safePage - 1) * rowsPerPage;
  const pageRows = tableData.slice(pageStartIndex, pageStartIndex + rowsPerPage);

  const toggleNode = (node: HierarchyNode) => {
    setActiveNodeId(node.id);

    if (node.children?.length) {
      setExpandedNodes((prev) => ({
        ...prev,
        [node.id]: !prev[node.id],
      }));
    }
  };

  const renderHierarchy = (nodes: HierarchyNode[]) => {
    return nodes.map((node) => {
      const hasChildren = !!node.children?.length;
      const isExpanded = !!expandedNodes[node.id];
      const isActive = activeNodeId === node.id;

      return (
        <div className={`level${node.level} item-children`} key={node.id}>
          <button
            type="button"
            className={`sidebar-item ${isActive ? "active" : ""}`}
            onClick={() => toggleNode(node)}
            style={{
              width: "100%",
              border: "none",
              background: "transparent",
              textAlign: "left",
              paddingLeft: "0px",
              paddingRight: "0px",
              cursor: "pointer",
            }}
          >
            <div className="sidebar-left">
              <span className="label">{node.label}</span>
            </div>

            <span className="count">{node.count}</span>
          </button>

          {hasChildren && isExpanded
            ? renderHierarchy(node.children || [])
            : null}
        </div>
      );
    });
  };

  type SidebarItemProps = {
    label: string;
    count: string;
    active?: boolean;
  };

  const SidebarItem = ({
    label,
    count,
    active = false,
  }: SidebarItemProps) => (
    <div
      className={`sidebar-item border-bottom radius-0 ${
        active ? "active" : ""
      }`}
    >
      <div className="sidebar-left">
        <span className="label">{label}</span>
      </div>

      <span className="count">{count}</span>
    </div>
  );

  type StatCardProps = {
    title: string;
    value: string | number;
  };

  const StatCard = ({ title, value }: StatCardProps) => (
    <div className="stat-card">
      <div className="stat-title">{title}</div>
      <div className="stat-value">{value}</div>
    </div>
  );

  return (
    <div className="setup-screen">
      <div className="top-stats">
        <StatCard
          title="Suppliers Classified"
          value={tableData.length.toLocaleString()}
        />
        <StatCard title="Materials Classified" value="48,214" />
        <StatCard title="Total Spend Classified" value="$2.68B" />
        <StatCard title="Human Review Required" value="1,204" />
      </div>

      <div className={`qc-layout${leftCollapsed ? " qc-layout--left-collapsed" : ""}${rightCollapsed ? " qc-layout--right-collapsed" : ""}`}>
        <div className={`qc-sidebar${leftCollapsed ? " is-collapsed" : ""}`}>
          <div className="border-bottom pad-12 qc-sidebar-head">
            {!leftCollapsed && (
              <div>
                <div className="sidebar-title">Taxonomy Hierarchy</div>
                <div className="sidebar-sub">Click a node to filter the grid.</div>
              </div>
            )}
            <button
              type="button"
              className="qc-sidebar-toggle"
              onClick={() => setLeftCollapsed((value) => !value)}
              aria-label={leftCollapsed ? "Expand taxonomy hierarchy" : "Collapse taxonomy hierarchy"}
              title={leftCollapsed ? "Expand" : "Collapse"}
            >
              {leftCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          </div>

          {!leftCollapsed && (
            <div className="sidebar-list pad-12">
              {renderHierarchy(taxonomyHierarchy)}
            </div>
          )}
        </div>

        <div className="qc-main">
          <div className="qc-filters">
            <div className="filter-title">FILTERS</div>

            <div className="filter-tabs">
              <span className="tab active">Confidence &lt; 90%</span>
              <span className="tab">Spend &gt; $10K</span>
            </div>

            <div className="filter-sort">
              Sort:
              <select>
                <option>Confidence</option>
              </select>
            </div>
          </div>

          <div className="qc-table-card">
            <div className="border-bottom pad-12">
              <div className="sidebar-title">Human review queue</div>
              <div className="sidebar-sub">Before human review</div>
            </div>

            <div className="table-actions pad-12 border-bottom">
              <button type="button" className="table-action-btn btn-success">
                Approve Parent
              </button>

              <button type="button" className="table-action-btn btn-danger">
                Reject
              </button>

              <button type="button" className="table-action-btn btn-outline">
                Edit grid
              </button>
            </div>

            {tableData.length === 0 ? (
              <div style={{ padding: 20 }}>
                ⚠️ No classification rows received
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="qc-table">
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th key={col}>
                          {col.replace(/ score$/, " Score")}
                          {aiGeneratedColumns.has(col) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="qc-header--ai__dot"
                                  aria-label="Generated by AI"
                                />
                              </TooltipTrigger>
                              <TooltipContent
                                side="bottom"
                                align="start"
                                sideOffset={10}
                                alignOffset={14}
                                className="qc-cell-tooltip"
                              >
                                Generated by AI
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {pageRows.map((row: TableRow, pageRowIndex: number) => {
                      const index = pageStartIndex + pageRowIndex;
                      return (
                        <tr
                          key={index}
                          style={{
                            height: "40px",
                            background: "#FFFFFF",
                          }}
                        >
                          {columns.map((col) => {
                            const cellValue = row[col];

                            return (
                              <td
                                key={col}
                                title={renderCellValue(cellValue)}
                                style={{
                                  maxWidth: "180px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {renderCellValue(cellValue)}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {tableData.length > 0 && (
              <div className="cmw-param-card__pagination pad-x-15">
                {totalPages > 1 && (
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            setPage((current) => Math.max(1, current - 1));
                          }}
                          aria-disabled={safePage === 1}
                          tabIndex={safePage === 1 ? -1 : 0}
                        />
                      </PaginationItem>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            href="#"
                            isActive={pageNumber === safePage}
                            onClick={(event) => {
                              event.preventDefault();
                              setPage(pageNumber);
                            }}
                          >
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            setPage((current) => Math.min(totalPages, current + 1));
                          }}
                          aria-disabled={safePage === totalPages}
                          tabIndex={safePage === totalPages ? -1 : 0}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
                <div className="cmw-param-card__pagination-size">
                  <label htmlFor="supplier-classification-rows-per-page">Rows per page</label>
                  <select
                    id="supplier-classification-rows-per-page"
                    value={rowsPerPage}
                    onChange={(event) => setRowsPerPage(Number(event.target.value))}
                  >
                    {[10, 20, 50, 100].map((count) => (
                      <option key={count} value={count}>
                        {count}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={`qc-right bg-white border border-color-d4 radius-10 gap-0${rightCollapsed ? " is-collapsed" : ""}`}>
          <div className="border-bottom pad-12 qc-sidebar-head">
            {!rightCollapsed && (
              <div>
                <div className="sidebar-title">Post Human Review</div>
                <div className="sidebar-sub">Live Summary</div>
              </div>
            )}
            <button
              type="button"
              className="qc-sidebar-toggle"
              onClick={() => setRightCollapsed((value) => !value)}
              aria-label={rightCollapsed ? "Expand live summary" : "Collapse live summary"}
              title={rightCollapsed ? "Expand" : "Collapse"}
            >
              {rightCollapsed ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
            </button>
          </div>

          {!rightCollapsed && (
            <div className="sidebar-list pad-12">
              <SidebarItem
                label="Suppliers Classified"
                count={tableData.length.toLocaleString()}
              />
              <SidebarItem label="Materials Classified" count="48,214" />
              <SidebarItem label="Total Spend Classified" count="$2.68B" />
              <SidebarItem label="Human Approved" count="982" />
              <SidebarItem label="Human Rejected" count="44" />
            </div>
          )}
        </div>
      </div>

      <div className="wizard-action-bar">
        <button
          type="button"
          className="action-bar__cancel btn btn--outline"
          onClick={onCancel}
        >
          Cancel
        </button>

        <button
          type="button"
          className="btn btn--outline m-left-auto"
          onClick={onBack}
        >
          <ChevronLeft size={14} /> Back
        </button>

        <button type="button" className="btn btn--primary" onClick={onNext}>
          Publish Dashboard <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

export default SupplierClassificationStep;